import type {
  AccountRechargeListItem,
  AccountRechargeListParams,
  AccountSubtractionData,
  BindCardRechargeOpParams,
  BindCardRechargeOpResponse,
  ClearPreviewListItem,
  ConfirmClearPreviewResponse,
  QueryClearPreviewListParams,
  RetryApprovedFundParams,
  UploadClearPreviewParams,
  UploadClearPreviewResponse,
} from './types'

import type { PagingRequest } from '#/api/type'
import type { MediaPlatform } from '#/types'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'
import { formatDateRange } from '#/utils'

interface CalculateChargeRateParams {
  amount: number
  convertCurrency: string
  originalCurrency: string
}

interface CalculateChargeRateRequest {
  amount: string
  convertCurrency: string
  originalCurrency: string
}

interface CalculateChargeRateResponse {
  chargeRate: string
  convertAmount: number
}

export const accountTransactionApi = defineApi('operation', ({ prefix }) => {
  function getAccountRechargeListParams(
    params: PagingRequest<AccountRechargeListParams>,
  ) {
    return {
      createdTime: formatDateRange(params.condition.createdTime),
      bizNo: params.condition.bizNo,
      accountId: params.condition.accountId,
      accountName: params.condition.accountName,
      isBindCardAccount: params.condition.isBindCardAccount,
      customerIds: params.condition.customerIds,
      userId: params.condition.userId,
      nickname: params.condition.nickname,
      agent: params.condition.agent,
      status: params.condition.status,
      type: params.condition.type,
      mediaPlatform: params.condition.mediaPlatform,
    }
  }
  // /gobestads/operation/wallet/listAdRecharge
  function getAccountRechargeList(
    params: PagingRequest<AccountRechargeListParams>,
  ) {
    params = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: getAccountRechargeListParams(params),
    }
    return requestClient
      .post(prefix('wallet/listAdRecharge'), params)
      .then((res) => {
        const items =
          res.list?.map((item: AccountRechargeListItem) => ({
            transactionSource: item.recordFromClient,
            createdTime: item.createdTime,
            bizNo: item.bizNo,
            userId: item.userId,
            nickname: item.nickname,
            createdBy: item.createdBy,
            approvalSubmitter: item.approvalSubmitter,
            email: item.email,
            agent: item.agent,
            accountId: item.accountId,
            accountName: item.accountName,
            isBindCardAccount: item.isBindCardAccount,
            snapshotCardInfo: item.snapshotCardInfo,
            bindCardHint: item.bindCardHint,
            currency: item.currency,
            mediaPlatform: item.mediaPlatform,
            status: item.status,
            amount: item.amount,
            customerId: item.customerId,
            rate: item.rate,
            agencyServiceRate: item.agencyServiceRate,
            totalServiceFee: item.totalServiceFee,
            ourCompanyServiceFee: item.ourCompanyServiceFee,
            agencyServiceFee: item.agencyServiceFee,
            actualAmount: item.actualAmount,
            baseAmount: item.baseAmount,
            prepaidRate: item.prepaidRate,
            prepaidFee: item.prepaidFee,
            balanceBefore: item.balanceBefore,
            customerCurrency: item.customerCurrency,
            customerAmount: item.customerAmount,
            completedAt: item.completedAt,
            clearDate: item.clearDate,
            remark: item.remark,
          })) || []
        return {
          items,
          total: res.total,
        }
      })
  }

  function exportAccountTransactionList(params: AccountRechargeListParams) {
    return requestClient
      .post(prefix('wallet/exportAdTransaction'), {
        condition: params,
      })
      .then((res) => {
        return {
          fileUrl: res?.downloadUrl,
          fileName: res?.fileName,
        }
      })
  }

  function bindCardRechargeOp(params: BindCardRechargeOpParams) {
    return requestClient
      .post<BindCardRechargeOpResponse>(
        prefix('wallet/bindCardRechargeOp'),
        params,
      )
      .then((res) => ({
        bizNo: res.bizNo,
        failed: res.failed,
        status: res.status,
      }))
  }

  /**
   * 飞书已通过后重试入账。
   * POST /wallet/retryApprovedFund
   */
  function retryApprovedFund(params: RetryApprovedFundParams) {
    return requestClient.post(prefix('wallet/retryApprovedFund'), {
      bizNo: params.bizNo,
    })
  }

  async function getAccountSubtractionList(
    params: PagingRequest<{
      companyId?: string
      account?: string
      mediaPlatform?: MediaPlatform[]
    }>,
  ) {
    params.condition = {
      ...params.condition,
      companyId: Number(params.condition.companyId),
    }
    console.log(params, 'params')
    return requestClient
      .post(prefix(`users/getUserBalanceAndAdsAccountPage`), params)
      .then((res: AccountSubtractionData) => {
        return {
          id: res.id,
          nickname: res.nickname,
          totalAvailableAmount: res.totalAvailableAmount,
          total: res.total,
          currency: res.currency,
          adsAccount: res.list?.map((item) => ({
            accountId: item.accountId,
            accountName: item.accountName,
            balance: item.balance,
            isOper: item.isOper,
            mediaPlatform: item.mediaPlatform,
            currency: item.currency,
            accountSource: item.accountSource,
            rate: item.rate,
            prepaidRate: item.prepaidRate,
            serviceRate: item.serviceRate,
            spendLast2Days: item.spendLast2Days,
            spendLast2DaysStatus: item.spendLast2DaysStatus,
          })),
        }
      })
  }

  function adReduced(params: {
    adAccounts: {
      accountId: string
      amount: string
      mediaPlatform?: MediaPlatform
    }[]
    totalAmount: string
    companyId: number
  }) {
    console.log(params, 'params')
    return requestClient.post(prefix('wallet/adReduced'), params)
  }

  function adRecharge(params: {
    adAccounts: {
      accountId: string
      amount: string
      mediaPlatform?: MediaPlatform
    }[]
    totalAmount: string
    companyId: number
  }) {
    return requestClient.post(prefix('wallet/adRecharge'), params)
  }

  function adClear(params: {
    adAccounts: {
      accountId: string
      mediaPlatform?: MediaPlatform
    }[]
    companyId: number
  }) {
    return requestClient.post(prefix('wallet/adClear'), params)
  }

  /**
   * 计算钱包扣款汇率及换算金额。
   * POST /wallet/calculateChargeRate
   */
  function calculateChargeRate(params: CalculateChargeRateParams) {
    const request: CalculateChargeRateRequest = {
      amount: String(params.amount),
      convertCurrency: params.convertCurrency,
      originalCurrency: params.originalCurrency,
    }

    return requestClient
      .post<CalculateChargeRateResponse>(
        prefix('wallet/calculateChargeRate'),
        request,
      )
      .then((res) => {
        return {
          convertAmount: res.convertAmount,
          chargeRate: res.chargeRate,
        }
      })
  }

  function uploadClearPreview(params: UploadClearPreviewParams) {
    const formData = new FormData()
    formData.append('bizCode', params.bizCode)
    formData.append('mediaPlatform', params.mediaPlatform)
    formData.append('file', params.file)

    return requestClient
      .post<UploadClearPreviewResponse>(
        prefix('dataUpload/syncPreview'),
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      .then((res) => ({
        totalCount: res?.totalCount,
        successCount: res?.successCount,
        failCount: res?.failCount,
        uploadLogId: res?.uploadLogId,
      }))
  }

  function queryClearPreviewList(params: QueryClearPreviewListParams) {
    return requestClient
      .post(prefix('dataUpload/queryPreviewList'), {
        uploadLogId: params.uploadLogId,
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        list: (res?.list || []).map((item: ClearPreviewListItem) => ({
          id: item.id,
          rowIndex: item.rowIndex,
          createdBy: item.createdBy,
          updatedBy: item.updatedBy,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          customerId: item.customerId,
          customerName: item.customerName,
          mediaPlatform: item.mediaPlatform,
          clearDate: item.clearDate,
          accountId: item.accountId,
          accountName: item.accountName,
          currency: item.currency,
          exchangeRate: item.exchangeRate,
          clearAmount: item.clearAmount,
          customerCurrency: item.customerCurrency,
          actualAmount: item.actualAmount,
          customerAmount: item.customerAmount,
          status: item.status,
          invalidReason: item.invalidReason,
        })),
        total: res?.total,
      }))
  }

  function confirmClearPreviewList(uploadLogId: number) {
    return requestClient
      .post<ConfirmClearPreviewResponse>(
        prefix('dataUpload/confirmPreviewList'),
        {
          uploadLogId,
        },
      )
      .then((res) => ({
        successCount: res?.successCount || 0,
        totalCount: res?.totalCount || 0,
      }))
  }

  function downloadClearTemplate(bizCode: string) {
    return requestClient
      .post(prefix('dataUpload/downloadTemplate'), { bizCode })
      .then((res) => res?.url)
  }

  return {
    getAccountRechargeList,
    bindCardRechargeOp,
    exportAccountTransactionList,
    getAccountSubtractionList,
    getAccountRechargeListParams,
    adReduced,
    adRecharge,
    adClear,
    calculateChargeRate,
    uploadClearPreview,
    queryClearPreviewList,
    confirmClearPreviewList,
    downloadClearTemplate,
    retryApprovedFund,
  }
})
